import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

// --- Types ---
export interface CookieConsent {
  /** Microsoft Clarity — mesure d'audience anonymisée (essentiel). */
  clarity: boolean;
  /** Google Analytics + Google Ads + Firebase Analytics (marketing). */
  marketing: boolean;
  /** Sentry — suivi des erreurs techniques (essentiel). */
  errorTracking: boolean;
}

interface CookieConsentState {
  consent: CookieConsent | null;
  showBanner: boolean;
  /** Tout accepter : Clarity + marketing (Google) + suivi des erreurs. */
  acceptAll: () => void;
  /** Uniquement les essentiels : Clarity + suivi des erreurs, PAS de Google. */
  acceptEssentials: () => void;
  /** Refuser : rien. */
  rejectAll: () => void;
  openBanner: () => void;
}

const STORAGE_KEY = 'fitd-cookie-consent';
// v2 : scinde l'ancien booléen `analytics` en `clarity` + `marketing`.
// Le bump invalide les consentements v1 → les visiteurs sont re-sollicités une fois.
const CONSENT_VERSION = 2;

interface StoredConsent {
  version: number;
  consent: CookieConsent;
  timestamp: string;
}

// --- Helpers ---
function readConsent(): CookieConsent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const stored: StoredConsent = JSON.parse(raw);
    if (stored.version !== CONSENT_VERSION) return null;
    return stored.consent;
  } catch {
    return null;
  }
}

function writeConsent(consent: CookieConsent): void {
  const stored: StoredConsent = {
    version: CONSENT_VERSION,
    consent,
    timestamp: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
}

// --- Script injection ---
// gtag.js (GA4 + Google Ads) est chargé en STATIC dans index.html avec
// Consent Mode v2 (default 'denied'). L'attribution source (gclid, referrer)
// est ainsi captée dès le premier paint, avant toute nav SPA. La gestion
// d'activation/désactivation passe uniquement par gtag('consent', 'update', ...)
// — voir updateGtagConsent() plus bas.

/**
 * Inject Microsoft Clarity (session recording + heatmaps).
 * Rangé dans les « essentiels » : chargé dès que `consent.clarity` est vrai
 * (mesure d'audience anonymisée, conforme nLPD). Indépendant du marketing Google.
 * Project ID: wit72stztv (footinthedoor.ch)
 */
function injectClarity(): void {
  if (typeof window === 'undefined') return;
  if (document.getElementById('clarity-script')) return;
  const ua = navigator.userAgent.toLowerCase();
  if (ua.includes('headless') || ua.includes('playwright') || navigator.webdriver ||
    window.location.hostname === 'localhost' || window.location.hostname.includes('--')) return;

  const doInject = () => {
    if (document.getElementById('clarity-script')) return;
    (function (c: any, l: Document, a: string, r: string, i: string) {
      c[a] = c[a] || function () { (c[a].q = c[a].q || []).push(arguments); };
      const t = l.createElement(r) as HTMLScriptElement;
      t.id = 'clarity-script';
      t.async = true;
      t.src = 'https://www.clarity.ms/tag/' + i;
      const y = l.getElementsByTagName(r)[0];
      y.parentNode?.insertBefore(t, y);
    })(window, document, 'clarity', 'script', 'wit72stztv');
  };

  const ric = (window as unknown as { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void }).requestIdleCallback;
  if (typeof ric === 'function') {
    ric(doInject, { timeout: 3000 });
  } else {
    setTimeout(doInject, 0);
  }
}

function removeClarity(): void {
  const script = document.getElementById('clarity-script');
  if (script) script.remove();
}

let firebaseAnalyticsLoaded = false;

async function loadFirebaseAnalytics(): Promise<void> {
  if (firebaseAnalyticsLoaded) return;
  try {
    const { getAnalytics } = await import('firebase/analytics');
    const { app } = await import('../firebase');
    getAnalytics(app);
    firebaseAnalyticsLoaded = true;
  } catch {
    console.warn('Firebase Analytics could not be loaded');
  }
}

/**
 * Update Google Consent Mode v2 signals. The static gtag in index.html
 * starts with all 'denied' so Google Ads sees the tag but doesn't write
 * cookies until the user accepts. Le consentement `marketing` (Google
 * Analytics + Ads) pilote ces signaux ; Clarity en est désormais découplé.
 */
function updateGtagConsent(marketing: boolean): void {
  const gtag = (window as any).gtag;
  if (typeof gtag !== 'function') return;
  gtag('consent', 'update', {
    'ad_storage': marketing ? 'granted' : 'denied',
    'ad_user_data': marketing ? 'granted' : 'denied',
    'ad_personalization': marketing ? 'granted' : 'denied',
    'analytics_storage': marketing ? 'granted' : 'denied',
  });
}

// --- Apply consent ---
function applyConsent(consent: CookieConsent): void {
  // Marketing = Google (GA4 + Ads + Firebase Analytics)
  updateGtagConsent(consent.marketing);
  if (consent.marketing) {
    loadFirebaseAnalytics();
  }

  // Clarity = essentiel, découplé du marketing
  if (consent.clarity) {
    injectClarity();
  } else {
    removeClarity();
  }

  // Sentry = essentiel technique
  (window as any).__fitd_sentry_consent = !!consent.errorTracking;
}

// --- Context ---
const CookieConsentContext = createContext<CookieConsentState | null>(null);

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsent] = useState<CookieConsent | null>(() => readConsent());
  const [showBanner, setShowBanner] = useState(() => readConsent() === null);

  useEffect(() => {
    if (consent) {
      // User has already made a choice — apply it
      applyConsent(consent);
    } else {
      // Aucun choix encore — niveau « essentiels » par défaut : gtag.js (GA4 + Ads)
      // reste en Consent Mode v2 denied (défaut index.html), et on charge Clarity
      // (anonymisé, nLPD) + on autorise Sentry. Tout est retiré si l'utilisateur refuse.
      injectClarity();
      (window as any).__fitd_sentry_consent = true;
    }
  }, []);

  const saveAndApply = useCallback((newConsent: CookieConsent) => {
    writeConsent(newConsent);
    setConsent(newConsent);
    applyConsent(newConsent);
    setShowBanner(false);
  }, []);

  const acceptAll = useCallback(() => {
    saveAndApply({ clarity: true, marketing: true, errorTracking: true });
  }, [saveAndApply]);

  const acceptEssentials = useCallback(() => {
    saveAndApply({ clarity: true, marketing: false, errorTracking: true });
  }, [saveAndApply]);

  const rejectAll = useCallback(() => {
    saveAndApply({ clarity: false, marketing: false, errorTracking: false });
  }, [saveAndApply]);

  const openBanner = useCallback(() => {
    setShowBanner(true);
  }, []);

  return (
    <CookieConsentContext.Provider value={{
      consent,
      showBanner,
      acceptAll,
      acceptEssentials,
      rejectAll,
      openBanner,
    }}>
      {children}
    </CookieConsentContext.Provider>
  );
}

export function useCookieConsent(): CookieConsentState {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error('useCookieConsent must be used within CookieConsentProvider');
  return ctx;
}
